import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import dayjs from 'dayjs';
import Input from '../../../components/Input';
import TextArea from '../../../components/TextArea';
import DatePicker from '../../../components/DatePicker';
import Select from '../../../components/Select';
import { formatDateForInput } from '../../../utils/dateUtils';
import { getDescriptionError, getLicenseCodeError, getLicenseKeyError } from '../../../utils/validation';

function AddLicenseModal({
  open,
  onClose,
  onSubmit,
  editMode = false,
  licenseData = null,
  initLicense = null,
  licenseTypes = [],
}) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    code: '',
    key: '',
    description: '',
    issueDate: '',
    expiryDate: '',
    licenseType: 0,
    maxUsers: '',
    maxDevices: '',
    maxGroupDevice: '',
    version: '',
    supportedFeatures: '',
  });

  const [errors, setErrors] = useState({
    code: '',
    key: '',
    description: '',
    issueDate: '',
    expiryDate: '',
    maxUsers: '',
    maxDevices: '',
    maxGroupDevice: '',
  });

  // Use ref to track description input without continuous state updates
  const descriptionRef = useRef('');

  // Refs for form fields to enable scrolling to errors
  const codeRef = useRef(null);
  const keyRef = useRef(null);
  const descriptionFieldRef = useRef(null);
  const issueDateRef = useRef(null);
  const expiryDateRef = useRef(null);
  const maxUsersRef = useRef(null);
  const maxDevicesRef = useRef(null);
  const maxGroupDeviceRef = useRef(null);

  // Helper functions to generate Code and Key
  const generateLicenseCode = () => {
    // Generate random alphanumeric string (8 chars)
    return Math.random().toString(36).substring(2, 10).toUpperCase();
  };

  const generateLicenseKey = () => {
    // Generate format: XXXX-XXXX-XXXX-XXXX (16 random alphanumeric chars in groups of 4)
    const segment = () => Math.random().toString(36).substring(2, 6).toUpperCase();
    return `${segment()}-${segment()}-${segment()}-${segment()}`;
  };

  useEffect(() => {
    if (editMode && licenseData) {
      const description = licenseData.description || '';
      setFormData({
        id: licenseData.id,
        code: licenseData.code || '',
        key: licenseData.key || '',
        description: description,
        issueDate: formatDateForInput(licenseData.issueDate),
        expiryDate: formatDateForInput(licenseData.expiryDate),
        licenseType: licenseData.licenseType ?? 0,
        maxUsers: licenseData.maxUsers ?? '',
        maxDevices: licenseData.maxDevices ?? '',
        maxGroupDevice: licenseData.maxGroupDevice ?? '',
        version: licenseData.version || '',
        supportedFeatures: licenseData.supportedFeatures || '',
      });
      descriptionRef.current = description;
    } else {
      // Add mode - auto-fill code, key, and dates
      const now = new Date();
      const oneYearLater = new Date();
      oneYearLater.setFullYear(now.getFullYear() + 1);

      setFormData({
        code: initLicense?.newCode || generateLicenseCode(),
        key: initLicense?.newKey || generateLicenseKey(),
        description: '',
        issueDate: formatDateForInput(now.toISOString()),
        expiryDate: formatDateForInput(oneYearLater.toISOString()),
        licenseType: 0,
        maxUsers: '',
        maxDevices: '',
        maxGroupDevice: '',
        version: '',
        supportedFeatures: '',
      });
      descriptionRef.current = '';
    }
  }, [editMode, licenseData, initLicense, open]);

  // Helper function to scroll to first error field
  const scrollToError = (newErrors) => {
    // Find first field with error and scroll to it
    const errorFields = [
      { name: 'code', ref: codeRef, hasError: !!newErrors.code },
      { name: 'key', ref: keyRef, hasError: !!newErrors.key },
      { name: 'description', ref: descriptionFieldRef, hasError: !!newErrors.description },
      { name: 'issueDate', ref: issueDateRef, hasError: !!newErrors.issueDate },
      { name: 'expiryDate', ref: expiryDateRef, hasError: !!newErrors.expiryDate },
      { name: 'maxUsers', ref: maxUsersRef, hasError: !!newErrors.maxUsers },
      { name: 'maxDevices', ref: maxDevicesRef, hasError: !!newErrors.maxDevices },
      { name: 'maxGroupDevice', ref: maxGroupDeviceRef, hasError: !!newErrors.maxGroupDevice },
    ];

    const firstErrorField = errorFields.find(field => field.hasError);

    if (firstErrorField && firstErrorField.ref.current) {
      // Scroll to the field
      firstErrorField.ref.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });

      // Focus the field after a short delay
      setTimeout(() => {
        const input = firstErrorField.ref.current.querySelector('input, textarea');
        if (input && !input.disabled) {
          input.focus();
        }
      }, 300);
    }
  };

  const getCapacityError = (value, { requiredCheck = false } = {}) => {
    if (value === '' || value === null || value === undefined) {
      return requiredCheck ? t('licenses.validation.required') : '';
    }

    const numericValue = Number(value);

    if (Number.isNaN(numericValue)) {
      return t('licenses.validation.integer');
    }

    if (!Number.isInteger(numericValue)) {
      return t('licenses.validation.integer');
    }

    if (numericValue < 0 || numericValue > 999999) {
      return t('licenses.validation.range', { min: 0, max: 999999 });
    }

    return '';
  };

  // Handlers for regenerate buttons
  const handleRegenerateCode = () => {
    setFormData((prev) => ({
      ...prev,
      code: generateLicenseCode(),
    }));
    // Clear code error
    setErrors((prev) => ({
      ...prev,
      code: '',
    }));
  };

  const handleRegenerateKey = () => {
    setFormData((prev) => ({
      ...prev,
      key: generateLicenseKey(),
    }));
    // Clear key error
    setErrors((prev) => ({
      ...prev,
      key: '',
    }));
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    const newValue = type === 'number' ? (value === '' ? '' : value) : value;

    // For description field, only update ref and validate in real-time
    if (name === 'description') {
      descriptionRef.current = newValue;

      // Validate description in real-time
      const newErrors = { ...errors };
      const descriptionError = getDescriptionError(newValue, t);
      if (descriptionError) {
        newErrors.description = descriptionError;
      } else {
        delete newErrors.description;
      }
      setErrors(newErrors);
      return;
    }

    // For other fields, update state normally
    const updatedFormData = {
      ...formData,
      [name]: newValue,
    };
    setFormData(updatedFormData);

    // Validate code and key in real-time
    const newErrors = { ...errors };

    if (name === 'code') {
      const codeError = getLicenseCodeError(newValue, t);
      if (codeError) {
        newErrors.code = codeError;
      } else {
        delete newErrors.code;
      }
    } else if (name === 'key') {
      const keyError = getLicenseKeyError(newValue, t);
      if (keyError) {
        newErrors.key = keyError;
      } else {
        delete newErrors.key;
      }
    } else if (['maxUsers', 'maxDevices', 'maxGroupDevice'].includes(name)) {
      const capacityError = getCapacityError(newValue, { requiredCheck: false });
      if (capacityError) {
        newErrors[name] = capacityError;
      } else {
        delete newErrors[name];
      }
    }

    // Validate dates in real-time when either date changes
    if (name === 'issueDate' || name === 'expiryDate') {
      const issueDate = name === 'issueDate' ? newValue : updatedFormData.issueDate;
      const expiryDate = name === 'expiryDate' ? newValue : updatedFormData.expiryDate;

      // Only validate if both dates are filled
      if (issueDate && expiryDate) {
        const issueDateObj = new Date(issueDate);
        const expiryDateObj = new Date(expiryDate);

        // Expiry date must be AFTER issue date (not equal)
        if (expiryDateObj <= issueDateObj) {
          newErrors.issueDate = t('licenses.addDialog.issueDateMustBeBeforeExpiryDate');
          newErrors.expiryDate = t('licenses.addDialog.expiryDateMustBeAfterIssueDate');
        } else {
          newErrors.issueDate = '';
          newErrors.expiryDate = '';
        }
      } else {
        // Clear errors if one of the dates is empty
        newErrors.issueDate = '';
        newErrors.expiryDate = '';
      }
    }

    setErrors(newErrors);
  };

  // Update description state only on blur
  const handleDescriptionBlur = () => {
    setFormData((prev) => ({
      ...prev,
      description: descriptionRef.current,
    }));
  };

  // Check if all required fields below (excluding code and key) are filled
  // In edit mode, always show code and key
  const shouldShowCodeAndKey = () => {
    if (editMode) return true;
    return (
      formData.issueDate !== '' &&
      formData.expiryDate !== '' &&
      formData.maxUsers !== '' &&
      formData.maxDevices !== '' &&
      formData.maxGroupDevice !== '' &&
      !errors.issueDate &&
      !errors.expiryDate &&
      !errors.maxUsers &&
      !errors.maxDevices &&
      !errors.maxGroupDevice
    );
  };

  // Check if form is valid (no errors and all required fields filled)
  const isFormValid = () => {
    return (
      formData.code.trim() !== '' &&
      formData.key.trim() !== '' &&
      formData.description.trim() !== '' &&
      formData.issueDate !== '' &&
      formData.expiryDate !== '' &&
      formData.maxUsers !== '' &&
      formData.maxDevices !== '' &&
      formData.maxGroupDevice !== '' &&
      !errors.code &&
      !errors.key &&
      !errors.issueDate &&
      !errors.expiryDate &&
      !errors.maxUsers &&
      !errors.maxDevices &&
      !errors.maxGroupDevice
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Ensure description is up-to-date from ref
    const finalFormData = {
      ...formData,
      description: descriptionRef.current,
    };

    // Validate on submit
    const newErrors = {
      code: '',
      key: '',
      description: '',
      issueDate: '',
      expiryDate: '',
      maxUsers: '',
      maxDevices: '',
      maxGroupDevice: '',
    };

    // Validate code
    const codeError = getLicenseCodeError(finalFormData.code, t);
    if (codeError) newErrors.code = codeError;

    // Validate key
    const keyError = getLicenseKeyError(finalFormData.key, t);
    if (keyError) newErrors.key = keyError;

    // Validate description
    const descriptionError = getDescriptionError(finalFormData.description, t);
    if (descriptionError) newErrors.description = descriptionError;

    // Check if both dates exist
    if (finalFormData.issueDate && finalFormData.expiryDate) {
      const issueDate = new Date(finalFormData.issueDate);
      const expiryDate = new Date(finalFormData.expiryDate);

      // Expiry date must be AFTER issue date (not equal)
      if (expiryDate <= issueDate) {
        newErrors.expiryDate = t('licenses.addDialog.expiryDateMustBeAfterIssueDate');
      }
    }

    // Update errors state
    setErrors(newErrors);

    // Validate capacity fields
    ['maxUsers', 'maxDevices', 'maxGroupDevice'].forEach((field) => {
      const capacityError = getCapacityError(finalFormData[field], { requiredCheck: true });
      if (capacityError) {
        newErrors[field] = capacityError;
      }
    });

    // Check if there are any errors
    const hasErrors =
      newErrors.code ||
      newErrors.key ||
      newErrors.description ||
      newErrors.issueDate ||
      newErrors.expiryDate ||
      newErrors.maxUsers ||
      newErrors.maxDevices ||
      newErrors.maxGroupDevice;

    if (hasErrors) {
      // Scroll to first error field
      scrollToError(newErrors);
    } else {
      // No errors, submit form
      onSubmit(finalFormData);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: 'primary.main',
        }}
      >
        {editMode ? <EditIcon /> : <AddIcon />}
        {editMode ? t('licenses.editDialog.title') : t('licenses.addDialog.title')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, pt: 2 }}>
          {/* Code and Key fields - always show but hide value until all required fields below are filled */}
          <Box ref={codeRef}>
            <Input
              label={t('licenses.addDialog.code')}
              name="code"
              value={shouldShowCodeAndKey() ? formData.code : ''}
              onChange={handleChange}
              required
              disabled
              error={!!errors.code}
              helperText={errors.code || (shouldShowCodeAndKey() ? t('licenses.addDialog.codeAutoGenerated') : '')}
              id="code"
              data-testid="license-modal-input-code"
              endAdornment={
                !editMode && shouldShowCodeAndKey() && (
                  <Tooltip title={t('licenses.addDialog.regenerateCode')}>
                    <IconButton
                      onClick={handleRegenerateCode}
                      size="small"
                      edge="end"
                      data-testid="license-modal-button-regenerateCode"
                    >
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )
              }
            />
          </Box>
          <Box ref={keyRef}>
            <Input
              label={t('licenses.addDialog.key')}
              name="key"
              value={shouldShowCodeAndKey() ? formData.key : ''}
              onChange={handleChange}
              required
              disabled
              error={!!errors.key}
              helperText={errors.key || (shouldShowCodeAndKey() ? t('licenses.addDialog.keyAutoGenerated') : '')}
              id="key"
              data-testid="license-modal-input-key"
              endAdornment={
                !editMode && shouldShowCodeAndKey() && (
                  <Tooltip title={t('licenses.addDialog.regenerateKey')}>
                    <IconButton
                      onClick={handleRegenerateKey}
                      size="small"
                      edge="end"
                      data-testid="license-modal-button-regenerateKey"
                    >
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )
              }
            />
          </Box>
          <Box sx={{ gridColumn: '1 / -1' }} ref={descriptionFieldRef}>
            <TextArea
              label={t('licenses.addDialog.description')}
              name="description"
              defaultValue={formData.description}
              onChange={handleChange}
              onBlur={handleDescriptionBlur}
              required
              rows={2}
              error={!!errors.description}
              helperText={errors.description}
              id="description"
              data-testid="license-modal-input-description"
            />
          </Box>
          <Box ref={issueDateRef}>
            <DatePicker
              label={t('licenses.addDialog.issueDate')}
              name="issueDate"
              value={formData.issueDate}
              onChange={handleChange}
              required
              maxDate={formData.expiryDate ? dayjs(formData.expiryDate).subtract(1, 'day') : undefined}
              error={!!errors.issueDate}
              helperText={errors.issueDate}
              id="issueDate"
              data-testid="license-modal-input-issueDate"
            />
          </Box>
          <Box ref={expiryDateRef}>
            <DatePicker
              label={t('licenses.addDialog.expiryDate')}
              name="expiryDate"
              value={formData.expiryDate}
              onChange={handleChange}
              required
              minDate={formData.issueDate ? dayjs(formData.issueDate).add(1, 'day') : undefined}
              error={!!errors.expiryDate}
              helperText={errors.expiryDate}
              id="expiryDate"
              data-testid="license-modal-input-expiryDate"
            />
          </Box>
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Select
              label={t('licenses.addDialog.licenseType')}
              name="licenseType"
              value={formData.licenseType}
              onChange={handleChange}
              options={licenseTypes}
              required
              id="licenseType"
              data-testid="license-modal-select-licenseType"
            />
          </Box>
          <Box ref={maxUsersRef}>
            <Input
              label={t('licenses.addDialog.maxUsers')}
              name="maxUsers"
              type="number"
              value={formData.maxUsers}
              onChange={handleChange}
              onBlur={(e) => {
                const capacityError = getCapacityError(e.target.value, { requiredCheck: true });
                setErrors((prev) => ({
                  ...prev,
                  maxUsers: capacityError,
                }));
              }}
              required
              slotProps={{
                htmlInput: {
                  min: 0,
                  max: 999999,
                },
              }}
              id="maxUsers"
              data-testid="license-modal-input-maxUsers"
              error={!!errors.maxUsers}
              helperText={errors.maxUsers || undefined}
            />
          </Box>
          <Box ref={maxDevicesRef}>
            <Input
              label={t('licenses.addDialog.maxDevices')}
              name="maxDevices"
              type="number"
              value={formData.maxDevices}
              onChange={handleChange}
              onBlur={(e) => {
                const capacityError = getCapacityError(e.target.value, { requiredCheck: true });
                setErrors((prev) => ({
                  ...prev,
                  maxDevices: capacityError,
                }));
              }}
              required
              slotProps={{
                htmlInput: {
                  min: 0,
                  max: 999999,
                },
              }}
              id="maxDevices"
              data-testid="license-modal-input-maxDevices"
              error={!!errors.maxDevices}
              helperText={errors.maxDevices || undefined}
            />
          </Box>
          <Box ref={maxGroupDeviceRef}>
            <Input
              label={t('licenses.addDialog.maxGroupDevice')}
              name="maxGroupDevice"
              type="number"
              value={formData.maxGroupDevice}
              onChange={handleChange}
              onBlur={(e) => {
                const capacityError = getCapacityError(e.target.value, { requiredCheck: true });
                setErrors((prev) => ({
                  ...prev,
                  maxGroupDevice: capacityError,
                }));
              }}
              required
              slotProps={{
                htmlInput: {
                  min: 0,
                  max: 999999,
                },
              }}
              id="maxGroupDevice"
              data-testid="license-modal-input-maxGroupDevice"
              error={!!errors.maxGroupDevice}
              helperText={errors.maxGroupDevice || undefined}
            />
          </Box>
          <Box>
            <Input
              label={t('licenses.addDialog.version')}
              name="version"
              value={formData.version}
              onChange={handleChange}
              helperText={t('licenses.addDialog.versionHelperText')}
              id="version"
              data-testid="license-modal-input-version"
            />
          </Box>
          <Box sx={{ gridColumn: '1 / -1' }}>
            <TextArea
              label={t('licenses.addDialog.supportedFeatures')}
              name="supportedFeatures"
              value={formData.supportedFeatures}
              onChange={handleChange}
              rows={3}
              helperText={t('licenses.addDialog.supportedFeaturesHelperText')}
              id="supportedFeatures"
              data-testid="license-modal-input-supportedFeatures"
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          id="cancel"
          name="cancel"
          data-testid="license-modal-button-cancel"
        >
          {t('licenses.addDialog.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={!isFormValid()}
          startIcon={editMode ? <EditIcon /> : <AddIcon />}
          id="confirm"
          name="confirm"
          data-testid="license-modal-button-confirm"
        >
          {editMode ? t('licenses.addDialog.update') : t('licenses.addDialog.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddLicenseModal;
